import React, { useState, useEffect } from 'react';
import { partnersAPI } from '../services/api';
import './Partners.css';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState({ partners: false, form: false });
  const [errors, setErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerFormData, setPartnerFormData] = useState({
    name: '',
    logo_url: '',
    type: '',
    website_url: ''
  });

  // Fetch partners from API
  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(prev => ({ ...prev, partners: true }));
    try {
      const response = await partnersAPI.getAll();
      setPartners(response.data.data || []);
      setErrors(prev => ({ ...prev, partners: null }));
    } catch (error) {
      console.error('Error fetching partners:', error);
      setErrors(prev => ({ ...prev, partners: error.message || 'Failed to fetch partners' }));
    } finally {
      setLoading(prev => ({ ...prev, partners: false }));
    }
  };

  // Validate partner form data
  const validatePartnerForm = () => {
    const errors = {};
    
    if (!partnerFormData.name.trim()) {
      errors.name = 'Partner name is required';
    }
    
    if (!partnerFormData.type.trim()) {
      errors.type = 'Partner type is required';
    }
    
    // Validate logo URL if provided
    if (partnerFormData.logo_url && !/^https?:\/\/.+\..+/.test(partnerFormData.logo_url)) {
      errors.logo_url = 'Logo URL must be a valid URL';
    }
    
    // Validate website URL if provided
    if (partnerFormData.website_url && !/^https?:\/\/.+\..+/.test(partnerFormData.website_url)) {
      errors.website_url = 'Website URL must be a valid URL';
    }
    
    return errors;
  };

  // Handle partner form input changes
  const handlePartnerInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setPartnerFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle partner form submission
  const handlePartnerFormSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validatePartnerForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setLoading(prev => ({ ...prev, form: true }));
    setErrors(prev => ({ ...prev, form: null }));
    
    try {
      if (editingPartner) {
        // Update existing partner
        await partnersAPI.update(editingPartner.id, partnerFormData);
      } else {
        // Create new partner
        await partnersAPI.create(partnerFormData);
      }
      
      // Reset form and refresh partners list
      resetPartnerForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      setErrors(prev => ({ ...prev, form: error.message || 'Failed to save partner' }));
    } finally {
      setLoading(prev => ({ ...prev, form: false }));
    }
  };

  // Handle edit partner
  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setPartnerFormData({
      name: partner.name || '',
      logo_url: partner.logo_url || '',
      type: partner.type || '',
      website_url: partner.website_url || ''
    });
    setShowPartnerForm(true);
  };

  // Handle delete partner
  const handleDeletePartner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) {
      return;
    }
    
    try {
      await partnersAPI.delete(id);
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      setErrors(prev => ({ ...prev, delete: error.message || 'Failed to delete partner' }));
    }
  };

  // Reset partner form
  const resetPartnerForm = () => {
    setPartnerFormData({
      name: '',
      logo_url: '',
      type: '',
      website_url: ''
    });
    setEditingPartner(null);
    setShowPartnerForm(false);
    setFormErrors({});
    setErrors(prev => ({ ...prev, form: null }));
  };

  return (
    <div className="partners-page">
      <div className="page-header">
        <h1>Partners Management</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={() => setShowPartnerForm(!showPartnerForm)}
          >
            {showPartnerForm ? 'Cancel' : '+ Add New Partner'}
          </button>
        </div>
      </div>

      {/* Error Messages */}
      {errors.partners && (
        <div className="error-message">
          Error loading partners: {errors.partners}
        </div>
      )}
      
      {errors.form && (
        <div className="error-message">
          Error saving partner: {errors.form}
        </div>
      )}
      
      {errors.delete && (
        <div className="error-message">
          Error deleting partner: {errors.delete}
        </div>
      )}

      {/* Partner Form */}
      {showPartnerForm && (
        <div className="partner-form-container">
          <h2>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</h2>
          <form onSubmit={handlePartnerFormSubmit} className="partner-form">
            <div className="form-group">
              <label htmlFor="name">Partner Name*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={partnerFormData.name}
                onChange={handlePartnerInputChange}
                className={formErrors.name ? 'error' : ''}
              />
              {formErrors.name && (
                <span className="error-text">{formErrors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="type">Partner Type*</label>
              <input
                type="text"
                id="type"
                name="type"
                value={partnerFormData.type}
                onChange={handlePartnerInputChange}
                className={formErrors.type ? 'error' : ''}
              />
              {formErrors.type && (
                <span className="error-text">{formErrors.type}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="logo_url">Logo URL</label>
              <input
                type="text"
                id="logo_url"
                name="logo_url"
                value={partnerFormData.logo_url}
                onChange={handlePartnerInputChange}
                placeholder="https://example.com/logo.png"
                className={formErrors.logo_url ? 'error' : ''}
              />
              {formErrors.logo_url && (
                <span className="error-text">{formErrors.logo_url}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="website_url">Website URL</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                value={partnerFormData.website_url}
                onChange={handlePartnerInputChange}
                placeholder="https://example.com"
                className={formErrors.website_url ? 'error' : ''}
              />
              {formErrors.website_url && (
                <span className="error-text">{formErrors.website_url}</span>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetPartnerForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading.form}>
                {loading.form ? 'Saving...' : (editingPartner ? 'Update Partner' : 'Add Partner')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Partners Table */}
      <div className="partners-list">
        <div className="table-header">
          <h2>Partners List</h2>
        </div>
        
        {loading.partners ? (
          <div className="loading-message">Loading partners...</div>
        ) : partners && partners.length > 0 ? (
          <table className="partners-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Logo</th>
                <th>Website</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td>{partner.type}</td>
                  <td>
                    {partner.logo_url ? (
                      <img src={partner.logo_url} alt={partner.name} className="partner-logo" />
                    ) : (
                      'No Logo'
                    )}
                  </td>
                  <td>
                    {partner.website_url ? (
                      <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                        Visit Website
                      </a>
                    ) : (
                      'No Website'
                    )}
                  </td>
                  <td>
                    <button 
                      className="btn btn-small btn-edit"
                      onClick={() => handleEditPartner(partner)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-small btn-delete"
                      onClick={() => handleDeletePartner(partner.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-partners">
            <p>No partners found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Partners;